/**
 * Unified Analytics Service — compat shim
 *
 * This file is kept for backward compatibility with existing call sites that
 * import from 'analyticsService'. New code should import directly from
 * 'analytics' (the typed facade).
 *
 * The shim forwards all calls to the analytics facade so there is a single
 * fan-out path to GA4, PostHog, and the server relay.
 *
 * ENFORCEMENT: In production, any event name NOT in VALID_EVENTS is dropped
 * with a console.error (surfaces in Sentry). In development it logs a
 * warning but still passes through so developers can see the event in the
 * debug overlay while building new features.
 */

import { analytics } from './analytics';
import { VALID_EVENTS } from './events';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('ANALYTICS_SERVICE');

const IS_PROD = import.meta.env.PROD;

export interface UnifiedAnalyticsEvent {
  name: string;
  category?: string;
  properties?: Record<string, any>;
  value?: number;
}

export interface UnifiedPageView {
  page_title?: string;
  page_path?: string;
  properties?: Record<string, any>;
}

export interface UnifiedUserProperties {
  user_id?: string;
  properties?: Record<string, any>;
}

class UnifiedAnalyticsService {
  track(event: UnifiedAnalyticsEvent): void {
    try {
      if (!VALID_EVENTS.has(event.name)) {
        if (IS_PROD) {
          // Drop in production — unknown events pollute funnels and bypass catalog enforcement
          log.error(
            `[analyticsService] Unknown event "${event.name}" dropped in production. ` +
            `Add it to EventMap + VALID_EVENTS before emitting.`
          );
          return;
        } else {
          log.warn(
            `[analyticsService] Unknown event "${event.name}" — add to EventMap + VALID_EVENTS.`
          );
        }
      }

      analytics.track(event.name as any, {
        ...(event.properties ?? {}),
        ...(event.category && { category: event.category }),
        ...(event.value !== undefined && { value: event.value }),
      });
    } catch (err) {
      log.warn('analyticsService.track error:', err);
    }
  }

  trackPageView(pageView: UnifiedPageView): void {
    analytics.page(pageView.page_path ?? window.location.pathname, {
      page_title: pageView.page_title,
      ...pageView.properties,
    });
  }

  setUserProperties(userProperties: UnifiedUserProperties): void {
    if (userProperties.user_id) {
      analytics.identify(userProperties.user_id, userProperties.properties ?? {});
    }
  }

  trackLogin(method: string, userId?: string): void {
    this.track({ name: 'login', category: 'engagement', properties: { method, user_id: userId } });
  }

  trackError(errorMessage: string, errorType: string, additionalData?: Record<string, any>): void {
    analytics.track('error_shown', { error_type: errorType, message: errorMessage, ...additionalData });
  }

  isReady(): { ga4: boolean } {
    return { ga4: true };
  }

  async flushEvents(): Promise<void> {
    await analytics.flush();
  }
}

const analyticsService = new UnifiedAnalyticsService();
export default analyticsService;
